import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { ApiUrlConstant } from "@constants/apiUrls";
import { verticalConfig } from "@config/verticalEnv";
import type { EvaluationTaskV3 } from "@services/evaluationService";

export interface ReportIssueRequest {
  studentId: string;
  examId: string;
  pageNumbers: string;
  reason: string;
  taskId: string;
}

export interface ReportIssueResponse {
  message?: string;
}

export interface ReportIssueContext {
  studentId: string;
  examId: string;
  taskId: string;
}

function getAccessToken(): string {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    throw new Error("Access token not found in localStorage");
  }
  return token;
}

function createMetaApiInstance() {
  const accessToken = getAccessToken();

  return axios.create({
    baseURL: ApiUrlConstant.metaUrl,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

export function getReportIssueContextFromTaskV3(
  task: EvaluationTaskV3
): ReportIssueContext {
  const submissionBy =
    task.exam_submission?.submission_by ?? task.submission_by ?? "";

  return {
    studentId: String(submissionBy),
    examId: task.exam_submission?.exam_id ?? "",
    taskId: task.id ?? "",
  };
}

export async function reportIssue(
  payload: ReportIssueRequest
): Promise<ReportIssueResponse> {
  try {
    const api = createMetaApiInstance();
    const response = await api.post<ReportIssueResponse>(
      "/report-issue",
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Error reporting issue:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to report issue")
    );
  }
}

export function useReportIssue() {
  return useMutation({
    mutationFn: (payload: ReportIssueRequest) => reportIssue(payload),
  });
}
